<template>
	<section v-if="isSectionContentFilled" class="car-rental-text-section">
		<div class="car-rental-text-section__content">
			<div v-if="sectionContent && sectionContent.content" class="car-rental-text-section__text rich-text">
				<template v-for="(node, index) in parsedContent" :key="index">
					<GlobalImage v-if="node.type === 'image'" :src="node.src || ''" :alt="node.alt || ''" quality="95" />
					<div v-else v-html="node.content"></div>
				</template>
			</div>
		</div>
	</section>
</template>

<script setup lang="ts">
import { parse, NodeType, HTMLElement as ParsedHTMLElement } from 'node-html-parser'

const { locale } = useI18n()
const pageTextSection = inject<Ref<ICMSLocationContent>>('pageTextSection')!
const sectionContent = ref<ICMSLocationContentTranslations>(
	pageTextSection.value?.translations.find((translation) => translation.language === locale.value)?.content[0],
)

const HEADING_OR_PARAGRAPH = /^(h[1-6]|p)$/i

// The editor can save headings/paragraphs with no text (e.g. `<h2></h2>`) or wrapping only an
// image (e.g. `<h2><img></h2>`, invalid HTML that also breaks a naive img-boundary text split).
// Parsing properly lets us drop truly empty tags and hoist image-only wrappers to plain image nodes.
const parsedContent = computed(() => {
	if (!sectionContent.value?.content || typeof sectionContent.value.content !== 'string') return []

	const nodes: Array<{ type: string; content?: string; src?: string; alt?: string }> = []
	const root = parse(sectionContent.value.content as string)

	root.childNodes.forEach((node) => {
		if (node.nodeType !== NodeType.ELEMENT_NODE) {
			const text = node.toString().trim()
			if (text) nodes.push({ type: 'text', content: text })
			return
		}

		const element = node as ParsedHTMLElement

		if (element.tagName === 'IMG') {
			nodes.push({ type: 'image', src: element.getAttribute('src') || '', alt: element.getAttribute('alt') || '' })
			return
		}

		const img = element.querySelector('img')
		const hasText = element.textContent.trim().length > 0

		if (img && !hasText) {
			nodes.push({ type: 'image', src: img.getAttribute('src') || '', alt: img.getAttribute('alt') || '' })
			return
		}

		if (!img && !hasText && HEADING_OR_PARAGRAPH.test(element.tagName)) return

		nodes.push({ type: 'text', content: element.toString() })
	})

	return nodes
})

const isSectionContentFilled = computed(() => {
	if (sectionContent.value) {
		return sectionContent.value.content !== '' && sectionContent.value.content !== null
	}
	return false
})
</script>

<style lang="scss" scoped src="./TextSection.scss"></style>
